import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { BOOL_VALUE } from "./ea.enums";
import { CODE_FUNCTION_NAME } from "./ea.runner.next.types";
import { PathValue } from "./ea.utils.types";
import { ISO_DATE_REGEX } from "./ea.regex";

function getNestedValue<T, Path extends string>(
  obj: T,
  path: Path,
): PathValue<T, Path> | undefined {
  if (typeof path.split !== "function") {
    console.warn(`getNestedValue path parameter ${path} is not a string`, path);
    return undefined;
  }

  // regex to handle arrays. Replace "systemData.values[0].isSensitive" to "systemData.values.0.isSensitive"
  const keys = path.replace(/\[(\d+)\]/g, ".$1").split(".") as string[];
  let result: any = obj;
  for (const key of keys) {
    if (result && key in result) {
      result = result[key];
    } else {
      const arrayKeyMatch = key.match(/^(.*)\[(\d+)\]$/);
      if (!arrayKeyMatch) return undefined;

      const [, arrayKey, arrayIndex] = arrayKeyMatch;
      if (arrayKey in result && result[arrayKey][arrayIndex]) {
        result = result[arrayKey][arrayIndex];
      } else {
        return undefined;
      }
    }
  }
  return result;
}

export const getBoolValue = (value: BOOL_VALUE) => {
  switch (value) {
    case BOOL_VALUE.TRUE:
      return true;
    case BOOL_VALUE.FALSE:
      return false;
    default:
      return value;
  }
};

export const getErrorMessage = (
  error: unknown,
  defaultMessage = "Something went wrong. Unknown error messag",
) => {
  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === "object" && "message" in error) {
    return error.message as string;
  }

  if (typeof error === "string") {
    return error;
  }

  return defaultMessage;
};

export const EA_UTILS = {
  object: {
    getNestedValue,
  },
};

type NonNull<T> = T extends null ? never : Exclude<T, null>;

export function nullToUndefined<T>(value: T | null): NonNull<T> | undefined {
  if (value === null) {
    return undefined;
  }
  return value as NonNull<T>;
}

export function isNotNull<T>(value: T | null): value is NonNull<T> {
  return value !== null;
}

export const transformAdvancedCodeToAsync = (code: string = "") => {
  const find = `function ${CODE_FUNCTION_NAME}`;
  const replaceWith = `async function ${CODE_FUNCTION_NAME}`;
  if (code.includes(find) && !code.includes(replaceWith)) {
    return code.replace(find, replaceWith);
  }
  return code;
};

export const convertValue = (value: string | number | Date) => {
  if (value === "" || value === undefined || value === null || typeof value === "number")
    return value;

  if (!isNaN(Number(value))) {
    return Number(value);
  }

  if (typeof value === "string" && ISO_DATE_REGEX.test(value)) {
    try {
      const dayjsValue = dayjs(value);
      return dayjsValue.isValid() ? dayjsValue : value;
    } catch (e) {
      return value;
    }
  }
  return value;
};

export const getDateIsoString = (dateString: string, format?: string) => {
  dayjs.extend(customParseFormat);
  return dayjs(dateString, format).toISOString();
};

export const createIsoString = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}T00:00:00.000Z`;
};
